import React from 'react';

// 24x24 viewBox.
// Two render modes:
//   mono:  single-color silhouette (used inside risk-colored bubbles).
//   brand: official brand colors (used in legends, badges, cluster labels).
//
// Children are returned (no <svg> wrapper) so callers can drop them into any svg.

/* A rounded-square badge carrying one letter. Used for vendors we have no
   licensed mark for. In mono mode the square is outlined and the glyph takes the
   caller's color, so it sits correctly inside a risk-colored bubble. */
const letterMark = (ch, bg, fg) => (
  bg === fg
    ? (
      <g fill="none" stroke={bg} strokeWidth="1.8">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <text x="12" y="16.5" textAnchor="middle" fontSize="11" fontWeight="700"
              fill={bg} stroke="none" fontFamily="system-ui, sans-serif">{ch}</text>
      </g>
    )
    : (
      <g>
        <rect x="3" y="3" width="18" height="18" rx="5" fill={bg} />
        <text x="12" y="16.5" textAnchor="middle" fontSize="11" fontWeight="700"
              fill={fg} fontFamily="system-ui, sans-serif">{ch}</text>
      </g>
    )
);

const ICONS = {
  copilot: {
    label: 'Microsoft 365 Copilot',
    mono: (color) => (
      <g fill={color}>
        <rect x="2"  y="2"  width="9" height="9" rx="1" />
        <rect x="13" y="2"  width="9" height="9" rx="1" />
        <rect x="2"  y="13" width="9" height="9" rx="1" />
        <rect x="13" y="13" width="9" height="9" rx="1" />
      </g>
    ),
    brand: () => (
      <g>
        <rect x="2"  y="2"  width="9" height="9" rx="1" fill="#F25022" />
        <rect x="13" y="2"  width="9" height="9" rx="1" fill="#7FBA00" />
        <rect x="2"  y="13" width="9" height="9" rx="1" fill="#00A4EF" />
        <rect x="13" y="13" width="9" height="9" rx="1" fill="#FFB900" />
      </g>
    ),
  },
  'copilot-studio': {
    label: 'Copilot Studio',
    // Microsoft brand mark — same as M365 Copilot.
    mono:  (color) => ICONS.copilot.mono(color),
    brand: ()      => ICONS.copilot.brand(),
  },
  'gemini-enterprise': {
    label: 'Gemini Enterprise',
    // Gemini 4-point spark.
    mono: (color) => (
      <path
        fill={color}
        d="M12 1 C12.6 6.2, 14 8.4, 17 9 C20 9.6, 22 10.6, 23 12 C22 13.4, 20 14.4, 17 15 C14 15.6, 12.6 17.8, 12 23 C11.4 17.8, 10 15.6, 7 15 C4 14.4, 2 13.4, 1 12 C2 10.6, 4 9.6, 7 9 C10 8.4, 11.4 6.2, 12 1 Z"
      />
    ),
    brand: () => (
      <g>
        <defs>
          <linearGradient id="aa-gem-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#4796E3" />
            <stop offset="0.5" stopColor="#9168C0" />
            <stop offset="1" stopColor="#E94335" />
          </linearGradient>
        </defs>
        <path
          fill="url(#aa-gem-grad)"
          d="M12 1 C12.6 6.2, 14 8.4, 17 9 C20 9.6, 22 10.6, 23 12 C22 13.4, 20 14.4, 17 15 C14 15.6, 12.6 17.8, 12 23 C11.4 17.8, 10 15.6, 7 15 C4 14.4, 2 13.4, 1 12 C2 10.6, 4 9.6, 7 9 C10 8.4, 11.4 6.2, 12 1 Z"
        />
      </g>
    ),
  },
  'gemini-workspace': {
    label: 'Gemini Workspace',
    // Same Gemini spark — workspace shares the brand mark.
    mono: (color) => ICONS['gemini-enterprise'].mono(color),
    brand: () => (
      <g>
        <defs>
          <linearGradient id="aa-gemws-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#4796E3" />
            <stop offset="0.5" stopColor="#34A853" />
            <stop offset="1" stopColor="#FBBC04" />
          </linearGradient>
        </defs>
        <path
          fill="url(#aa-gemws-grad)"
          d="M12 1 C12.6 6.2, 14 8.4, 17 9 C20 9.6, 22 10.6, 23 12 C22 13.4, 20 14.4, 17 15 C14 15.6, 12.6 17.8, 12 23 C11.4 17.8, 10 15.6, 7 15 C4 14.4, 2 13.4, 1 12 C2 10.6, 4 9.6, 7 9 C10 8.4, 11.4 6.2, 12 1 Z"
        />
      </g>
    ),
  },
  custom: {
    label: 'Custom Agents',
    // Simple robot head — antenna, square head, two eyes.
    mono: (color) => (
      <g fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 V5" />
        <circle cx="12" cy="2.4" r="0.8" fill={color} stroke="none" />
        <rect x="4" y="6" width="16" height="13" rx="2.5" />
        <circle cx="9"  cy="12.5" r="1.4" fill={color} stroke="none" />
        <circle cx="15" cy="12.5" r="1.4" fill={color} stroke="none" />
        <path d="M9 16 H15" />
      </g>
    ),
    brand: () => ICONS.custom.mono('#A855F7'),
  },
  shadow: {
    label: 'Endpoint Agents',
    // Domino / incognito mask — universally reads as "shadow".
    mono: (color) => (
      <g fill={color}>
        <path d="M2 9 C2 7, 4 6, 7 6 H17 C20 6, 22 7, 22 9 V12 C22 15, 19 18, 16 18 C14.4 18, 13.2 17.2, 12 15.6 C10.8 17.2, 9.6 18, 8 18 C5 18, 2 15, 2 12 Z" />
        <circle cx="8"  cy="11.5" r="1.4" fill="#0b0f1a" />
        <circle cx="16" cy="11.5" r="1.4" fill="#0b0f1a" />
      </g>
    ),
    brand: () => ICONS.shadow.mono('#F59E0B'),
  },
  'cloud-discovery': {
    label: 'Cloud Discovery',
    mono: (color) => (
      <g fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 18 H18.5 A3.5 3.5 0 0 0 19 11 A7 7 0 0 0 5.5 9.5 A4.5 4.5 0 0 0 7 18 Z" />
      </g>
    ),
    brand: () => ICONS['cloud-discovery'].mono('#38BDF8'),
  },
  // ---- Model-provider surfaces ----------------------------------------------
  // These three had no mark, so an Anthropic or OpenAI node was indistinguishable
  // from any other. Rather than approximate a trademark from memory and ship a
  // subtly wrong logo, each is a lettermark in the vendor's own brand color --
  // unmistakable at 12px, which is the size these actually render at, and honest
  // about being our badge rather than their logo.
  anthropic: {
    label: 'Anthropic',
    mono: (color) => letterMark('A', color, color),
    brand: () => letterMark('A', '#D97757', '#FFFFFF'),
  },
  openai: {
    label: 'OpenAI',
    mono: (color) => letterMark('O', color, color),
    brand: () => letterMark('O', '#10A37F', '#FFFFFF'),
  },
  cursor: {
    label: 'Cursor',
    mono: (color) => letterMark('C', color, color),
    brand: () => letterMark('C', '#6E56CF', '#FFFFFF'),
  },
  // ---- Cloud surfaces -------------------------------------------------------
  gcp: {
    label: 'GCP Agents',
    // Google Cloud hexagon mark
    mono: (color) => (
      <g fill={color}>
        <path d="M12 3 L19 7 V17 L12 21 L5 17 V7 Z" opacity="0.9" />
        <rect x="10" y="10" width="4" height="4" rx="0.5" fill="rgba(0,0,0,0.3)" />
      </g>
    ),
    brand: () => (
      <g>
        <path d="M12 3 L19 7 V17 L12 21 L5 17 V7 Z" fill="#4285F4" />
        <path d="M12 3 L19 7 L12 11 L5 7 Z" fill="#34A853" opacity="0.85" />
        <rect x="10" y="10" width="4" height="4" rx="0.5" fill="#ffffff" opacity="0.9" />
      </g>
    ),
  },
  aws: {
    label: 'AWS Agents',
    // AWS smile mark (simplified)
    mono: (color) => (
      <g fill={color}>
        <rect x="4" y="5" width="16" height="10" rx="2" opacity="0.9" />
        <path d="M6 17 Q12 20 18 17" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </g>
    ),
    brand: () => (
      <g>
        <rect x="4" y="5" width="16" height="10" rx="2" fill="#FF9900" />
        <path d="M6 17 Q12 20.5 18 17" fill="none" stroke="#FF9900" strokeWidth="2.2" strokeLinecap="round" />
        <text x="12" y="13" fontFamily="Arial,sans-serif" fontSize="7" fontWeight="900" textAnchor="middle" fill="#ffffff">AWS</text>
      </g>
    ),
  },
  azure: {
    label: 'Azure Agents',
    // Azure stylized "A" / wave mark
    mono: (color) => (
      <g fill={color}>
        <path d="M4 18 L9 5 L14 14 L11 14 L16 18 Z" opacity="0.9" />
      </g>
    ),
    brand: () => (
      <g>
        <defs>
          <linearGradient id="aa-azure-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0078D4" />
            <stop offset="1" stopColor="#50E6FF" />
          </linearGradient>
        </defs>
        <path d="M4 18 L9 5 L14 14 L11 14 L16 18 Z" fill="url(#aa-azure-grad)" />
      </g>
    ),
  },
};

export const surfaceIconLabel = (surface) => ICONS[surface]?.label || surface;

/**
 * React component for rendering a surface icon at any size.
 *   <SurfaceIcon surface="copilot" size={14} />              brand colors
 *   <SurfaceIcon surface="copilot" size={14} mono color="#fff" />  monochrome
 *   <SurfaceIcon surface="copilot" decorative className="x" />    beside its own name
 *
 * `decorative` is for a mark sitting next to text that already names the
 * product (a sidebar heading): it is hidden from assistive tech and carries no
 * tooltip, so the name is announced once rather than twice.
 */
export const SurfaceIcon = ({ surface, size = 16, mono = false, color = 'currentColor', style, title, label: labelProp, className, decorative = false }) => {
  // Aliases too: Atlas answers "endpoint" for the surface these icons call
  // "shadow" (renamed 2026-09-29), and a missing icon fails silently.
  const def = ICONS[surface] || ICONS[resolveSurface(surface)];
  if (!def) return null;
  const body = mono ? def.mono(color) : def.brand();
  if (decorative) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={className}
        style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', ...style }}
        aria-hidden="true"
        focusable="false"
      >
        {body}
      </svg>
    );
  }
  // `title={false}` drops the native tooltip for a caller that draws its own,
  // without dropping the accessible name.
  const suppressed = title === false;
  const label = labelProp || (suppressed ? def.label : (title || def.label));
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', ...style }}
      aria-label={label}
      role="img"
    >
      {/* An icon that replaced a name has to be able to give it back. The
          aria-label alone does that for a screen reader and for nobody else;
          <title> is what a pointer reveals. */}
      {!suppressed && <title>{label}</title>}
      {body}
    </svg>
  );
};

/**
 * d3 helper — returns the inner SVG markup string for a surface icon, scaled
 * to the requested pixel size. Caller is responsible for translation.
 *
 * Usage:
 *   const html = surfaceIconMarkup('copilot', 16, { mono: true, color: '#fff' });
 *   selection.append('g').html(html);
 */
export const surfaceIconMarkup = (surface, size = 16, { mono = false, color = '#fff' } = {}) => {
  // Render the React element to a static markup string via a hidden mounting trick
  // — but to avoid pulling in react-dom/server, we instead inline the SVG paths
  // directly here for the d3 path. Keep the structures in sync with ICONS above.
  const k = size / 24;
  const t = `translate(${-12 * k},${-12 * k}) scale(${k})`;

  switch (surface) {
    case 'copilot':
    case 'copilot-studio':
      // Both Microsoft surfaces render the four-square brand mark.
      if (mono) {
        return `<g transform="${t}" fill="${color}">
          <rect x="2"  y="2"  width="9" height="9" rx="1"/>
          <rect x="13" y="2"  width="9" height="9" rx="1"/>
          <rect x="2"  y="13" width="9" height="9" rx="1"/>
          <rect x="13" y="13" width="9" height="9" rx="1"/>
        </g>`;
      }
      return `<g transform="${t}">
        <rect x="2"  y="2"  width="9" height="9" rx="1" fill="#F25022"/>
        <rect x="13" y="2"  width="9" height="9" rx="1" fill="#7FBA00"/>
        <rect x="2"  y="13" width="9" height="9" rx="1" fill="#00A4EF"/>
        <rect x="13" y="13" width="9" height="9" rx="1" fill="#FFB900"/>
      </g>`;

    case 'gemini-enterprise':
    case 'gemini-workspace':
      return `<g transform="${t}">
        <path fill="${color}" d="M12 1 C12.6 6.2, 14 8.4, 17 9 C20 9.6, 22 10.6, 23 12 C22 13.4, 20 14.4, 17 15 C14 15.6, 12.6 17.8, 12 23 C11.4 17.8, 10 15.6, 7 15 C4 14.4, 2 13.4, 1 12 C2 10.6, 4 9.6, 7 9 C10 8.4, 11.4 6.2, 12 1 Z"/>
      </g>`;

    case 'custom':
      return `<g transform="${t}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 2 V5"/>
        <circle cx="12" cy="2.4" r="0.8" fill="${color}" stroke="none"/>
        <rect x="4" y="6" width="16" height="13" rx="2.5"/>
        <circle cx="9"  cy="12.5" r="1.4" fill="${color}" stroke="none"/>
        <circle cx="15" cy="12.5" r="1.4" fill="${color}" stroke="none"/>
        <path d="M9 16 H15"/>
      </g>`;

    case 'shadow':
      return `<g transform="${t}" fill="${color}">
        <path d="M2 9 C2 7, 4 6, 7 6 H17 C20 6, 22 7, 22 9 V12 C22 15, 19 18, 16 18 C14.4 18, 13.2 17.2, 12 15.6 C10.8 17.2, 9.6 18, 8 18 C5 18, 2 15, 2 12 Z"/>
        <circle cx="8"  cy="11.5" r="1.4" fill="#0b0f1a"/>
        <circle cx="16" cy="11.5" r="1.4" fill="#0b0f1a"/>
      </g>`;

    case 'gcp':
      return `<g transform="${t}" fill="${color}">
        <path d="M12 3 L19 7 V17 L12 21 L5 17 V7 Z" opacity="0.9"/>
        <rect x="10" y="10" width="4" height="4" rx="0.5" fill="rgba(0,0,0,0.3)"/>
      </g>`;

    case 'aws':
      return `<g transform="${t}">
        <rect x="4" y="5" width="16" height="10" rx="2" fill="${color}"/>
        <path d="M6 17 Q12 20 18 17" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
      </g>`;

    case 'azure':
      return `<g transform="${t}" fill="${color}">
        <path d="M4 18 L9 5 L14 14 L11 14 L16 18 Z" opacity="0.9"/>
      </g>`;

    default:
      return '';
  }
};

// ---------------------------------------------------------------------------
// Per-application icons.
//
// Inside the Universe, each bubble is a specific app (e.g. "PowerPoint" or
// "drive") rather than a whole surface. Rendering the surface mark on every
// bubble loses that signal. The resolver below maps an agent name to one of
// the per-app marks; callers fall back to the surface icon if no match.
//
// All marks are 0..24 viewBox monochrome silhouettes designed to sit inside
// a risk-colored bubble. Office-family apps use the standard brand pattern
// (rounded white tile + dark letter mark); Workspace apps use a recognizable
// silhouette of the product's logo.
// ---------------------------------------------------------------------------

// Microsoft Office, Google Workspace, and consumer-AI brand palettes
// (modern flat marks). Solid colors only — no gradients — so that d3-injected
// markup can't collide on a shared <defs> id when many bubbles render together.
const BRAND = {
  word:       { body: '#185ABD', fold: '#103F81' },
  excel:      { body: '#107C41', fold: '#0B5C30' },
  powerpoint: { body: '#C43E1C', fold: '#8C2A12' },
  onenote:    { body: '#7719AA', fold: '#5A1380' },
  outlook:    '#0078D4',
  teams:      '#4B53BC',
  asana:      '#F06A6A',
  docs:       { body: '#4285F4', fold: '#1B66C9' },
  sheets:     { body: '#0F9D58', fold: '#0B7C45' },
  slides:     { body: '#F4B400', fold: '#C99100' },
  meet:       '#00897B',

  // Endpoint / shadow-AI brand colors.
  chatgpt:    '#10A37F',  // OpenAI green
  claude:     '#D97757',  // Anthropic coral
  github:     '#181717',  // GitHub black
  gemini:     '#4796E3',  // Gemini blue
  slack:      '#FFFFFF',  // Slack tile is white; the four marks carry color
  deepseek:   '#4D6BFE',  // DeepSeek blue
  perplexity: '#20808D',  // Perplexity teal
  poe:        '#5746EC',  // Poe purple
  mistral:    '#F54900',  // Mistral orange
  cursor:     '#0F0F0F',  // Cursor black
};

/**
 * Microsoft Office document-shaped tile: folded-corner page in the brand body
 * color, darker triangle for the fold, white letter centered below the fold.
 * Matches Microsoft's modern Word / Excel / PowerPoint / OneNote app marks.
 */
const officeDoc = (letter, brand, t, fontSize = 9) => `<g transform="${t}">
  <path d="M5 3 H14 L19 8 V20 C19 20.6, 18.6 21, 18 21 H5 C4.4 21, 4 20.6, 4 20 V4 C4 3.4, 4.4 3, 5 3 Z"
        fill="${brand.body}"/>
  <path d="M14 3 V8 H19 Z" fill="${brand.fold}"/>
  <text x="11.5" y="17.6"
        font-family="-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif"
        font-size="${fontSize}" font-weight="900" text-anchor="middle"
        fill="#ffffff">${letter}</text>
</g>`;

/** Google Workspace document tile (Docs/Sheets/Slides share the same shape). */
const workspaceDoc = (brand, t, innerSvg) => `<g transform="${t}">
  <path d="M6 3 H14 L19 8 V20 C19 20.6, 18.6 21, 18 21 H6 C5.4 21, 5 20.6, 5 20 V4 C5 3.4, 5.4 3, 6 3 Z"
        fill="${brand.body}"/>
  <path d="M14 3 V8 H19 Z" fill="${brand.fold}"/>
  ${innerSvg}
</g>`;

/**
 * Brand-colored rounded tile with a white letter mark — used as the default
 * fallback for shadow-AI detectors that don't get a hand-drawn icon. Two-char
 * labels auto-shrink so "DS", "AZ", "v0" still fit.
 */
const letterTile = (color, label, t, opts = {}) => {
  const len = String(label).length;
  const { textColor = '#ffffff', fontSize = len >= 2 ? 7.5 : 11 } = opts;
  return `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${color}"/>
    <text x="12" y="${len >= 2 ? 15.5 : 16.4}"
          font-family="-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif"
          font-size="${fontSize}" font-weight="900" text-anchor="middle"
          fill="${textColor}">${label}</text>
  </g>`;
};

const APP_MARKUP = {
  // ---- Microsoft Office ----------------------------------------------------
  ms_word:       (_c, t) => officeDoc('W', BRAND.word,       t, 8),
  ms_excel:      (_c, t) => officeDoc('X', BRAND.excel,      t),
  ms_powerpoint: (_c, t) => officeDoc('P', BRAND.powerpoint, t),
  ms_onenote:    (_c, t) => officeDoc('N', BRAND.onenote,    t),

  // Outlook — blue tile, stylized "O" + envelope flap.
  ms_outlook: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.outlook}"/>
    <ellipse cx="9.5" cy="12" rx="3.3" ry="3.8" fill="none" stroke="#ffffff" stroke-width="1.7"/>
    <path d="M14 9.5 H19 V17 C19 17.55, 18.55 18, 18 18 H14 Z" fill="#ffffff"/>
    <path d="M14 9.5 L16.5 12 L19 9.5" stroke="${BRAND.outlook}" stroke-width="1.1" fill="none"/>
  </g>`,

  // Teams — purple tile with a white "T" mark.
  ms_teams: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.teams}"/>
    <rect x="7"  y="8"  width="10" height="1.8" fill="#ffffff"/>
    <rect x="11.1" y="8" width="1.8" height="9"   fill="#ffffff"/>
  </g>`,

  // Microsoft 365 Copilot ribbon — multi-color stylized swoosh used on Loop /
  // Copilot marks. Solid colors (no gradients) so multiple bubbles don't
  // collide on a shared <defs> id when rendered via d3's .html().
  ms_m365chat: (_c, t) => `<g transform="${t}">
    <path d="M4 14 C4 9, 8 5, 13 5 C17 5, 20 7, 20 7 L17 11 C17 11, 15.5 9.5, 13 9.5 C10.2 9.5, 8 11.7, 8 14 Z"
          fill="#26A0DA"/>
    <path d="M20 10 C20 15, 16 19, 11 19 C7 19, 4 17, 4 17 L7 13 C7 13, 8.5 14.5, 11 14.5 C13.8 14.5, 16 12.3, 16 10 Z"
          fill="#0078D4"/>
    <path d="M11 19 C13 19, 14.5 19.6, 16 21 C14.5 21.5, 12.5 21.7, 11 21.7 C8.5 21.7, 6.5 21, 5.2 19.6 C7 19.2, 9 19, 11 19 Z"
          fill="#50E6FF"/>
  </g>`,

  // Asana — three coral dots forming a triangle.
  ms_asana: (_c, t) => `<g transform="${t}" fill="${BRAND.asana}">
    <circle cx="12"   cy="6.5"  r="3.2"/>
    <circle cx="6.5"  cy="16"   r="3.2"/>
    <circle cx="17.5" cy="16"   r="3.2"/>
  </g>`,

  // ---- Google Workspace ----------------------------------------------------
  // Drive — three colored triangles forming the Drive trefoil.
  ws_drive: (_c, t) => `<g transform="${t}">
    <path d="M9 3 H15 L22 15 H16 Z"                fill="#FFCD46"/>
    <path d="M2 18 L8 8 L11.5 13.7 L7 21.4 Z"      fill="#34A853"/>
    <path d="M7 21.4 H22 L19 16 H10 Z"             fill="#1A73E8"/>
  </g>`,

  // Docs / Sheets / Slides — Google's standard document shape with the iconic
  // inner detail (lines / grid / slide) drawn in white.
  ws_docs: (_c, t) => workspaceDoc(BRAND.docs, t, `
    <g stroke="#ffffff" stroke-width="1.1" stroke-linecap="round">
      <line x1="8" y1="12" x2="16" y2="12"/>
      <line x1="8" y1="15" x2="16" y2="15"/>
      <line x1="8" y1="18" x2="13" y2="18"/>
    </g>
  `),
  ws_sheets: (_c, t) => workspaceDoc(BRAND.sheets, t, `
    <g stroke="#ffffff" stroke-width="1.1">
      <rect x="8" y="11" width="8" height="7" fill="none"/>
      <line x1="8" y1="14.5" x2="16" y2="14.5"/>
      <line x1="12" y1="11"  x2="12" y2="18"/>
    </g>
  `),
  ws_slides: (_c, t) => workspaceDoc(BRAND.slides, t, `
    <rect x="8" y="12" width="8" height="6" rx="0.5"
          fill="none" stroke="#ffffff" stroke-width="1.2"/>
  `),

  // Gmail — red/white M-fold envelope.
  ws_gmail: (_c, t) => `<g transform="${t}">
    <rect x="3" y="6.5" width="18" height="12.5" rx="1.5" fill="#ffffff"/>
    <path d="M3 6.5 L12 13.5 L21 6.5 H21.5 C22 6.5, 22 7, 22 7.5 V8 L12 15 L2 8 V7.5 C2 7, 2 6.5, 2.5 6.5 Z"
          fill="#EA4335"/>
    <path d="M3 7 L3 19 H7 V12 Z"  fill="#C5221F"/>
    <path d="M21 7 L21 19 H17 V12 Z" fill="#C5221F"/>
    <path d="M7 12 L12 15.5 L17 12 V19 H7 Z" fill="#ffffff"/>
  </g>`,

  // Meet — green tile + white video lens.
  ws_meet: (_c, t) => `<g transform="${t}">
    <rect x="3" y="7" width="13.5" height="10" rx="1.5" fill="${BRAND.meet}"/>
    <path d="M16.5 10 L21 7.5 V16.5 L16.5 14 Z" fill="${BRAND.meet}"/>
    <circle cx="9.5" cy="12" r="2.6" fill="#ffffff"/>
  </g>`,

  // ---- Endpoint / shadow-AI tools ------------------------------------------

  // ChatGPT / OpenAI — green tile with a stylized six-fold hex (nautilus motif).
  sh_chatgpt: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.chatgpt}"/>
    <path d="M12 6 L17 9 V15 L12 18 L7 15 V9 Z"
          fill="none" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M12 6 V18 M7 9 L17 15 M17 9 L7 15"
          stroke="#ffffff" stroke-width="0.8" opacity="0.65"/>
  </g>`,

  // Claude / Anthropic — coral tile with white starburst (Anthropic mark).
  sh_claude: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.claude}"/>
    <g stroke="#ffffff" stroke-width="1.7" stroke-linecap="round">
      <line x1="12"   y1="6"  x2="12"   y2="18"/>
      <line x1="6"    y1="12" x2="18"   y2="12"/>
      <line x1="8.2"  y1="8.2" x2="15.8" y2="15.8"/>
      <line x1="15.8" y1="8.2" x2="8.2"  y2="15.8"/>
    </g>
    <circle cx="12" cy="12" r="1.4" fill="${BRAND.claude}"/>
  </g>`,

  // GitHub Copilot — dark tile with the Copilot "face" silhouette (rounded
  // helmet, two eye dots).
  sh_github_copilot: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.github}"/>
    <path d="M12 7 C 9 7, 7 9, 7 12 V15 C 7 16.5, 8 17, 9 17 H15 C 16 17, 17 16.5, 17 15 V12 C 17 9, 15 7, 12 7 Z"
          fill="none" stroke="#ffffff" stroke-width="1.4"/>
    <circle cx="10" cy="13" r="0.95" fill="#ffffff"/>
    <circle cx="14" cy="13" r="0.95" fill="#ffffff"/>
  </g>`,

  // Consumer Gemini — same four-point spark as Workspace/Enterprise but in a
  // single blue brand color (no gradient → no <defs> id collisions).
  sh_gemini: (_c, t) => `<g transform="${t}">
    <path fill="${BRAND.gemini}"
          d="M12 1 C12.6 6.2, 14 8.4, 17 9 C20 9.6, 22 10.6, 23 12 C22 13.4, 20 14.4, 17 15 C14 15.6, 12.6 17.8, 12 23 C11.4 17.8, 10 15.6, 7 15 C4 14.4, 2 13.4, 1 12 C2 10.6, 4 9.6, 7 9 C10 8.4, 11.4 6.2, 12 1 Z"/>
  </g>`,

  // Slack — white tile with the four-color pinwheel.
  sh_slack: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#ffffff"
          stroke="rgba(15,23,42,0.15)" stroke-width="0.8"/>
    <rect x="10"  y="5"    width="4" height="7"  rx="2" fill="#36C5F0"/>
    <rect x="12"  y="10"   width="7" height="4"  rx="2" fill="#2EB67D"/>
    <rect x="10"  y="12"   width="4" height="7"  rx="2" fill="#ECB22E"/>
    <rect x="5"   y="10"   width="7" height="4"  rx="2" fill="#E01E5A"/>
  </g>`,

  // DeepSeek — blue tile + simplified whale silhouette.
  sh_deepseek: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.deepseek}"/>
    <path d="M5 13 C 7 10.5, 10 10.5, 12 12 C 14.5 10, 18 11, 19 13 C 17.5 14, 15 14, 12.5 13 C 10 14, 7 14, 5 13 Z"
          fill="#ffffff"/>
    <circle cx="17.5" cy="12.4" r="0.7" fill="${BRAND.deepseek}"/>
  </g>`,

  // Perplexity — teal tile with a stylized "?" / spark.
  sh_perplexity: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.perplexity}"/>
    <path d="M9 10 C 9 7.5, 11 6.5, 12 6.5 C 13 6.5, 15 7.2, 15 9.5 C 15 11, 13.5 11.7, 12.5 12.4 C 12 12.8, 12 13.4, 12 14"
          fill="none" stroke="#ffffff" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="12" cy="17" r="1.1" fill="#ffffff"/>
  </g>`,

  // Poe (Quora) — purple tile with a chat-burst mark.
  sh_poe: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.poe}"/>
    <path d="M6 13 C 6 9.5, 8.5 7, 12 7 C 15.5 7, 18 9.5, 18 13 C 18 16, 15.5 18, 12 18 L8 19.5 L9 16.5 C 7 15.5, 6 14.5, 6 13 Z"
          fill="#ffffff"/>
  </g>`,

  // Mistral — orange tile with a stylized lattice (suggests Mistral's mark).
  sh_mistral: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.mistral}"/>
    <g fill="#ffffff">
      <rect x="6"  y="7"  width="3" height="10"/>
      <rect x="10.5" y="7" width="3" height="10"/>
      <rect x="15"  y="7"  width="3" height="10"/>
    </g>
  </g>`,

  // Cursor — black tile with a white "C" wedge / pointer.
  sh_cursor: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="${BRAND.cursor}"/>
    <path d="M9 7 L17 12 L9 17 Z" fill="#ffffff"/>
  </g>`,

  // Microsoft Copilot (consumer) — same multi-cyan ribbon as the M365 mark,
  // distinguished from the embedded Microsoft 365 Copilot by surface context.
  sh_ms_copilot: (_c, t) => APP_MARKUP.ms_m365chat(_c, t),
  // Microsoft 365 Copilot (embedded productivity, separate detector) — alias.
  sh_ms365_copilot: (_c, t) => APP_MARKUP.ms_m365chat(_c, t),

  // Google Gemini API surfaces share the consumer-Gemini mark.
  sh_google_ai_api: (_c, t) => APP_MARKUP.sh_gemini(_c, t),

  // HuggingFace — yellow tile with a simple smiling face (the 🤗 mark).
  sh_huggingface: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#FFD21E"/>
    <circle cx="9" cy="11" r="1.1" fill="#111"/>
    <circle cx="15" cy="11" r="1.1" fill="#111"/>
    <path d="M8 14 C 9 16, 11 17, 12 17 C 13 17, 15 16, 16 14"
          fill="none" stroke="#111" stroke-width="1.4" stroke-linecap="round"/>
  </g>`,
  sh_huggingface_api: (_c, t) => APP_MARKUP.sh_huggingface(_c, t),

  // Grok / xAI — black tile with the "X" mark.
  sh_grok: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#000000"/>
    <path d="M7 8 L17 16 M17 8 L7 16"
          stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
  </g>`,

  // Midjourney — black tile with a stylized sailboat (their iconic mark).
  sh_midjourney: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#000000"/>
    <path d="M12 6 L12 13 L7 13 Z M12 13 L17 13 L12 6 Z"
          fill="#ffffff" opacity="0.95"/>
    <path d="M5 16 H19 L17 18 H7 Z" fill="#ffffff"/>
  </g>`,

  // Notion AI — white tile with bold "N" outline (matches Notion's actual mark).
  sh_notion_ai: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3"
          fill="#ffffff" stroke="rgba(15,23,42,0.18)" stroke-width="0.8"/>
    <text x="12" y="17.2"
          font-family="Georgia,serif" font-size="14" font-weight="900"
          text-anchor="middle" fill="#0F1015">N</text>
  </g>`,

  // Snowflake — blue tile with the iconic 6-point snowflake.
  sh_snowflake: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#29B5E8"/>
    <g stroke="#ffffff" stroke-width="1.4" stroke-linecap="round">
      <line x1="12" y1="6"  x2="12" y2="18"/>
      <line x1="6.7" y1="9" x2="17.3" y2="15"/>
      <line x1="6.7" y1="15" x2="17.3" y2="9"/>
    </g>
  </g>`,

  // Grammarly — green tile with white check mark.
  sh_grammarly: (_c, t) => `<g transform="${t}">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="#15C39A"/>
    <path d="M7.5 12.5 L11 16 L17 9"
          stroke="#ffffff" stroke-width="2" stroke-linecap="round"
          stroke-linejoin="round" fill="none"/>
  </g>`,

  // ----- Generic letter-tile detectors (brand color + 1-2 char mark) --------
  // Direct chat
  sh_you:            (_c, t) => letterTile('#4F46E5', 'Y',  t),
  sh_phind:          (_c, t) => letterTile('#2563EB', 'Φ',  t),
  sh_meta_ai:        (_c, t) => letterTile('#0064E0', 'M',  t),
  sh_cohere_coral:   (_c, t) => letterTile('#F94D4D', 'C',  t),
  sh_character_ai:   (_c, t) => letterTile('#0F1015', 'C',  t),
  sh_pi_ai:          (_c, t) => letterTile('#635BFF', 'π',  t),
  sh_mistral_le_chat:(_c, t) => letterTile(BRAND.mistral, 'M', t),

  // Code assistants
  sh_replit_ai:      (_c, t) => letterTile('#F26207', 'R',  t),
  sh_codeium:        (_c, t) => letterTile('#09B6A2', 'C',  t),
  sh_tabnine:        (_c, t) => letterTile('#6B41DB', 'T',  t),
  sh_v0:             (_c, t) => letterTile('#000000', 'v0', t),
  sh_bolt:           (_c, t) => letterTile('#0F0F0F', '⚡', t, { fontSize: 13 }),
  sh_lovable:        (_c, t) => letterTile('#FF4D8D', '♥',  t, { fontSize: 13 }),
  sh_windsurf:       (_c, t) => letterTile('#00CFCB', 'W',  t),
  sh_amazon_q:       (_c, t) => letterTile('#FF9900', 'Q',  t),

  // Local runtimes and AI desktop apps
  sh_lm_studio:      (_c, t) => letterTile('#7C3AED', 'LM', t),
  sh_ollama:         (_c, t) => letterTile('#0F0F0F', 'O',  t),
  sh_anythingllm:    (_c, t) => letterTile('#4C5EFF', 'A',  t),
  sh_jan:            (_c, t) => letterTile('#1F2937', 'J',  t),
  sh_zed:            (_c, t) => letterTile('#084CCF', 'Z',  t),
  sh_warp:           (_c, t) => letterTile('#01A0B4', 'W',  t),
  sh_superwhisper:   (_c, t) => letterTile('#111827', 'SW', t),

  // Meetings and transcription
  sh_otter_ai:       (_c, t) => letterTile('#0A84FF', 'O',  t),
  sh_fireflies:      (_c, t) => letterTile('#8B5CF6', 'F',  t),
  sh_granola:        (_c, t) => letterTile('#C2703A', 'G',  t),

  // Managed model platforms
  sh_vertex_ai:      (_c, t) => letterTile('#4285F4', 'V',  t),

  // Writing & content
  sh_jasper:         (_c, t) => letterTile('#FF7400', 'J',  t),
  sh_copy_ai:        (_c, t) => letterTile('#5A50FF', 'C',  t),
  sh_writesonic:     (_c, t) => letterTile('#8E5BFF', 'W',  t),

  // Image / creative
  sh_dall_e:         (_c, t) => letterTile('#10A37F', 'D',  t),
  sh_stable_diffusion:(_c, t) => letterTile('#FF6B00', 'SD', t),
  sh_leonardo:       (_c, t) => letterTile('#6B4FE8', 'L',  t),
  sh_canva:          (_c, t) => letterTile('#00C4CC', 'C',  t),
  sh_suno:           (_c, t) => letterTile('#000000', '♪',  t, { fontSize: 13 }),
  sh_udio:           (_c, t) => letterTile('#1A1A1A', 'U',  t),
  sh_runway:         (_c, t) => letterTile('#000000', 'R',  t),
  sh_elevenlabs:     (_c, t) => letterTile('#000000', 'II', t),
  sh_gamma:          (_c, t) => letterTile('#6042EE', 'Γ',  t),

  // Embedded productivity
  sh_google_docs_ai: (_c, t) => APP_MARKUP.ws_docs(_c, t),
  sh_databricks:     (_c, t) => letterTile('#FF3621', 'DB', t),
  sh_salesforce:     (_c, t) => letterTile('#00A1E0', 'SF', t),

  // APIs — generic letter tile in vendor color, "API" suffix implied by surface.
  sh_openai_api:     (_c, t) => letterTile('#10A37F', 'API', t, { fontSize: 6.5 }),
  sh_anthropic_api:  (_c, t) => letterTile('#D97757', 'API', t, { fontSize: 6.5 }),
  sh_azure_openai_api:(_c, t) => letterTile('#0078D4', 'AZ', t),
  sh_cohere_api:     (_c, t) => letterTile('#F94D4D', 'C',  t),
  sh_replicate_api:  (_c, t) => letterTile('#000000', 'R',  t),
  sh_mistral_api:    (_c, t) => letterTile(BRAND.mistral, 'M', t),
  sh_deepseek_api:   (_c, t) => letterTile(BRAND.deepseek, 'DS', t),
  sh_groq_api:       (_c, t) => letterTile('#F55036', 'G',  t),
  sh_together_api:   (_c, t) => letterTile('#00B894', 'T',  t),
  sh_fireworks_api:  (_c, t) => letterTile('#FF6A2C', 'F',  t),
  sh_bedrock_api:    (_c, t) => letterTile('#FF9900', 'B',  t),
};

/**
 * Resolve an agent's display name → app icon id for the given surface.
 * Returns null when no app match is found; callers should fall back to the
 * surface icon. Matching is intentionally generous (substring, case-insensitive)
 * because the upstream name fields vary (e.g. "IPM.SkypeTeams.Message.Copilot.BizChat"
 * vs "Teams Copilot", or "drive" vs "Google Drive").
 */
export const resolveAppIconId = (surface, name) => {
  const n = String(name || '').toLowerCase();
  if (!n) return null;

  if (surface === 'copilot' || surface === 'copilot-studio') {
    if (n.includes('powerpoint') || n.includes('ppt'))                  return 'ms_powerpoint';
    if (n.includes('excel'))                                            return 'ms_excel';
    if (/\bword\b/.test(n) || n.includes('word copilot'))               return 'ms_word';
    if (n.includes('onenote'))                                          return 'ms_onenote';
    if (n.includes('outlook'))                                          return 'ms_outlook';
    if (n.includes('teams') || n.includes('skypeteams'))                return 'ms_teams';
    if (n.includes('365 chat') || n === 'microsoft 365 chat' ||
        n.includes('m365 chat') || n.includes('bizchat'))               return 'ms_m365chat';
    if (n.includes('asana'))                                            return 'ms_asana';
    return null;
  }
  if (surface === 'gemini-workspace') {
    if (n.includes('drive'))                       return 'ws_drive';
    if (n.includes('sheet'))                       return 'ws_sheets';
    if (n.includes('slide'))                       return 'ws_slides';
    if (n.includes('doc'))                         return 'ws_docs';
    if (n.includes('gmail') || n.includes('mail')) return 'ws_gmail';
    if (n.includes('meet'))                        return 'ws_meet';
    return null;
  }
  if (surface === 'shadow' || surface === 'endpoint') {
    return resolveShadowIcon(n);
  }
  if (surface === 'gcp' || surface === 'aws' || surface === 'azure') {
    return resolveCloudAgentIcon(surface, n);
  }
  return null;
};

/**
 * Shadow / endpoint tool resolver. Matched in order — most specific patterns
 * first so e.g. "Microsoft 365 Copilot" never collapses into the generic
 * "Microsoft Copilot" detector. Mirrors the 60-detector catalog in
 * endpoint-agent-monitoring/browser-extension/src/lib/shadow-ai-detector/patterns.ts.
 */
const SHADOW_MATCHERS = [
  // ---- Embedded productivity (most-specific Microsoft and Google first) ---
  { match: ['microsoft 365 copilot', 'm365 copilot'], iconId: 'sh_ms365_copilot' },
  { match: ['github copilot', 'github-copilot'],      iconId: 'sh_github_copilot' },
  { match: ['microsoft copilot', 'ms copilot'],       iconId: 'sh_ms_copilot' },
  { match: ['google docs ai', 'google docs'],         iconId: 'sh_google_docs_ai' },
  { match: ['slack'],                                 iconId: 'sh_slack' },
  { match: ['databricks'],                            iconId: 'sh_databricks' },
  { match: ['snowflake', 'cortex'],                   iconId: 'sh_snowflake' },
  { match: ['salesforce', 'einstein'],                iconId: 'sh_salesforce' },

  // ---- Direct chat (specific named API first) ------------------------------
  { match: ['chatgpt'],                               iconId: 'sh_chatgpt' },
  { match: ['claude'],                                iconId: 'sh_claude' },
  { match: ['google gemini', 'gemini', 'bard'],       iconId: 'sh_gemini' },
  { match: ['perplexity'],                            iconId: 'sh_perplexity' },
  { match: ['poe'],                                   iconId: 'sh_poe' },
  { match: ['you.com'],                               iconId: 'sh_you' },
  { match: ['phind'],                                 iconId: 'sh_phind' },
  { match: ['huggingface inference api', 'huggingface api'], iconId: 'sh_huggingface_api' },
  { match: ['huggingface', 'hugging face'],           iconId: 'sh_huggingface' },
  { match: ['deepseek api'],                          iconId: 'sh_deepseek_api' },
  { match: ['deepseek'],                              iconId: 'sh_deepseek' },
  { match: ['grok', 'xai'],                           iconId: 'sh_grok' },
  { match: ['mistral le chat', 'le chat'],            iconId: 'sh_mistral_le_chat' },
  { match: ['mistral api'],                           iconId: 'sh_mistral_api' },
  { match: ['mistral'],                               iconId: 'sh_mistral' },
  { match: ['meta ai', 'meta-ai'],                    iconId: 'sh_meta_ai' },
  { match: ['cohere coral'],                          iconId: 'sh_cohere_coral' },
  { match: ['cohere api'],                            iconId: 'sh_cohere_api' },
  { match: ['cohere'],                                iconId: 'sh_cohere_coral' },
  { match: ['character.ai', 'character ai'],          iconId: 'sh_character_ai' },
  // Pi is a 2-letter name — match it as a whole word, not via includes().
  { match: ['pi.ai', 'heypi', 'inflection'], exact: ['pi'], iconId: 'sh_pi_ai' },

  // ---- Code assistants ----------------------------------------------------
  { match: ['replit'],                                iconId: 'sh_replit_ai' },
  { match: ['cursor'],                                iconId: 'sh_cursor' },
  { match: ['codeium'],                               iconId: 'sh_codeium' },
  { match: ['tabnine'],                               iconId: 'sh_tabnine' },
  { match: ['v0.dev', 'v0 ', 'vercel v0'],            iconId: 'sh_v0' },
  { match: ['bolt.new', 'stackblitz bolt'],           iconId: 'sh_bolt' },
  { match: ['lovable'],                               iconId: 'sh_lovable' },
  { match: ['windsurf'],                              iconId: 'sh_windsurf' },
  { match: ['amazon q'],                              iconId: 'sh_amazon_q' },

  // ---- Local runtimes and AI desktop apps ---------------------------------
  // Every name here was observed on real sensor data with no mark of its own,
  // so each one was drawing the generic shadow glyph. The short ones match
  // `exact` as well as by pattern, because a bare "jan", "zed" or "warp" is a
  // word that turns up inside sentences and dates.
  { match: ['lm studio', 'lmstudio'],                 iconId: 'sh_lm_studio' },
  { match: ['ollama'],                                iconId: 'sh_ollama' },
  { match: ['anythingllm', 'anything llm'],           iconId: 'sh_anythingllm' },
  { match: ['jan.ai'], exact: ['jan'],                iconId: 'sh_jan' },
  { match: ['zed editor', 'zed.dev'], exact: ['zed'], iconId: 'sh_zed' },
  { match: ['warp terminal', 'warp.dev'], exact: ['warp'], iconId: 'sh_warp' },
  { match: ['superwhisper'],                          iconId: 'sh_superwhisper' },

  // ---- Meetings and transcription -----------------------------------------
  { match: ['otter.ai', 'otter ai'],                  iconId: 'sh_otter_ai' },
  { match: ['fireflies'],                             iconId: 'sh_fireflies' },
  { match: ['granola'],                               iconId: 'sh_granola' },

  // ---- Managed model platforms --------------------------------------------
  { match: ['vertex ai', 'vertex'],                   iconId: 'sh_vertex_ai' },

  // ---- Writing & content --------------------------------------------------
  { match: ['jasper'],                                iconId: 'sh_jasper' },
  { match: ['copy.ai', 'copy ai'],                    iconId: 'sh_copy_ai' },
  { match: ['writesonic'],                            iconId: 'sh_writesonic' },
  { match: ['grammarly'],                             iconId: 'sh_grammarly' },
  { match: ['notion ai', 'notion'],                   iconId: 'sh_notion_ai' },

  // ---- Image / creative ---------------------------------------------------
  { match: ['midjourney'],                            iconId: 'sh_midjourney' },
  { match: ['dall-e', 'dall e', 'dalle'],             iconId: 'sh_dall_e' },
  { match: ['stable diffusion', 'dreamstudio', 'stability'], iconId: 'sh_stable_diffusion' },
  { match: ['leonardo'],                              iconId: 'sh_leonardo' },
  { match: ['canva'],                                 iconId: 'sh_canva' },
  { match: ['suno'],                                  iconId: 'sh_suno' },
  { match: ['udio'],                                  iconId: 'sh_udio' },
  { match: ['runway'],                                iconId: 'sh_runway' },
  { match: ['elevenlabs'],                            iconId: 'sh_elevenlabs' },
  { match: ['gamma'],                                 iconId: 'sh_gamma' },

  // ---- API detectors (vendor-specific APIs first so generic "openai api"
  //      doesn't swallow "azure openai api") -------------------------------
  { match: ['azure openai', 'azure-openai'],          iconId: 'sh_azure_openai_api' },
  { match: ['openai api', 'openai-api'],              iconId: 'sh_openai_api' },
  { match: ['openai'],                                iconId: 'sh_chatgpt' },
  { match: ['anthropic api', 'anthropic-api'],        iconId: 'sh_anthropic_api' },
  { match: ['anthropic'],                             iconId: 'sh_claude' },
  { match: ['google ai api', 'generative language', 'gemini api'], iconId: 'sh_google_ai_api' },
  { match: ['replicate'],                             iconId: 'sh_replicate_api' },
  { match: ['groq'],                                  iconId: 'sh_groq_api' },
  { match: ['together ai', 'together.ai'],            iconId: 'sh_together_api' },
  { match: ['fireworks ai', 'fireworks.ai'],          iconId: 'sh_fireworks_api' },
  { match: ['bedrock', 'aws bedrock'],                iconId: 'sh_bedrock_api' },
];

/**
 * A pattern matches on word boundaries, not as a bare substring.
 *
 * `n.includes(p)` gave "LM Studio" the Udio mark — "udio" sits inside
 * "studio" — and a wrong icon is worse than a missing one: it names the tool
 * as a different vendor. Every short entry in the table sets the same trap
 * (poe in "poetry", grok in "grokking", zed, xai, jan), and the entries are
 * product names, so a boundary is what was meant all along.
 *
 * No lookbehind: it is not available everywhere this bundle runs, so the
 * boundary is a matched-and-discarded character class on each side.
 */
const BOUNDARY_RE = new Map();

const boundaryRe = (p) => {
  let re = BOUNDARY_RE.get(p);
  if (!re) {
    const esc = p.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    re = new RegExp(`(^|[^a-z0-9])${esc}([^a-z0-9]|$)`);
    BOUNDARY_RE.set(p, re);
  }
  return re;
};

const resolveShadowIcon = (n) => {
  for (const m of SHADOW_MATCHERS) {
    if (m.exact && m.exact.includes(n)) return m.iconId;
    if (m.match.some((p) => boundaryRe(p).test(n))) return m.iconId;
  }
  return null;
};

// Cloud agent icon resolvers — map well-known cloud AI agent names to icons.
const resolveCloudAgentIcon = (surface, n) => {
  // GCP-specific agents
  if (surface === 'gcp') {
    if (n.includes('smolagent') || n === 'smolagents')   return 'sh_huggingface_api';
    if (n.includes('litellm'))                           return 'sh_openai_api';
    if (n.includes('vertex') || n.includes('aiplatform')) return 'sh_google_ai_api';
    if (n.includes('discoveryengine') || n.includes('discovery_engine')) return 'sh_google_ai_api';
    if (n.includes('gemini'))                            return 'sh_gemini';
  }
  // AWS-specific agents
  if (surface === 'aws') {
    if (n.includes('bedrock'))                           return 'sh_bedrock_api';
    if (n.includes('amazon q') || n.includes('amazonq')) return 'sh_amazon_q';
    if (n.includes('sagemaker'))                         return 'sh_bedrock_api';
  }
  // Azure-specific agents
  if (surface === 'azure') {
    if (n.includes('azure openai') || n.includes('azure-openai')) return 'sh_azure_openai_api';
    if (n.includes('openai'))                            return 'sh_openai_api';
    if (n.includes('copilot'))                           return 'sh_ms_copilot';
  }
  // Cross-cloud: litellm, langchain, smolagents can appear in any cloud
  if (n.includes('litellm'))                             return 'sh_openai_api';
  if (n.includes('smolagent'))                           return 'sh_huggingface_api';
  if (n.includes('openai'))                              return 'sh_chatgpt';
  if (n.includes('anthropic') || n.includes('claude'))  return 'sh_claude';
  return null;
};

/**
 * Returns SVG markup for an agent bubble. Resolves to a per-app icon when
 * we recognize the application; otherwise falls back to the surface icon.
 */
export const appIconMarkup = (surface, name, size = 16, opts = {}) => {
  const { color = '#fff' } = opts;
  const id = resolveAppIconId(surface, name);
  if (!id) return surfaceIconMarkup(surface, size, { mono: true, color });
  const k = size / 24;
  const t = `translate(${-12 * k},${-12 * k}) scale(${k})`;
  const renderer = APP_MARKUP[id];
  return renderer ? renderer(color, t) : surfaceIconMarkup(surface, size, { mono: true, color });
};

/**
 * React component version of appIconMarkup — for table rows / pills where a
 * proper React element is more natural than a d3-injected markup string.
 * Falls back to <SurfaceIcon> when no per-app match is found.
 */
/** SVG <title> is markup, so a tool name with a bracket in it has to be escaped. */
const escapeXml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

export const AppIcon = ({ surface, name, size = 16, color = 'currentColor', style, title }) => {
  const id = resolveAppIconId(surface, name);
  // `title={false}` suppresses the <title> element — and ONLY that. A caller
  // that draws its own tooltip needs this, because the browser's native one
  // would otherwise fire alongside it, a second later and in the OS style.
  // The aria-label stays either way: it is what names the icon for a screen
  // reader, and a custom tooltip does not replace it.
  const suppressed = title === false;
  const label = (suppressed ? name : (title || name)) || undefined;
  if (!id) {
    return (
      <SurfaceIcon
        surface={surface}
        size={size}
        mono
        color={color}
        style={style}
        title={suppressed ? false : label}
        label={label}
      />
    );
  }
  // Identity, NOT appIconMarkup's translate/scale.
  //
  // The renderers draw in 0-24 space and wrap themselves in
  // `<g transform="${t}">`. appIconMarkup exists for d3, which appends into a
  // bare <g> with no viewBox, so there the transform has to do the scaling
  // and centre the art on the origin. This component also sets
  // viewBox="0 0 24 24", which already maps those 24 units onto `size` — so
  // applying the transform as well scaled the art a second time AND moved its
  // centre to (0,0), the top-left corner. Three quarters of every icon was
  // clipped outside the viewBox, which read as "too small to see".
  const t = 'translate(0,0) scale(1)';
  const renderer = APP_MARKUP[id];
  // The artwork arrives as a markup string, and an element cannot carry both
  // dangerouslySetInnerHTML and children — so <title> goes into the string.
  // Without it the icon is a mark with no name anywhere a pointer can reach,
  // which is the whole cost of replacing a text column with glyphs.
  const titleTag = (label && !suppressed) ? `<title>${escapeXml(label)}</title>` : '';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', ...style }}
      role="img"
      aria-label={label}
      dangerouslySetInnerHTML={{ __html: `${titleTag}${renderer(color, t)}` }}
    />
  );
};

export default SurfaceIcon;

/* ---------------------------------------------------------------------------
 * Surface aliases
 *
 * The same vendor arrives under different spellings depending on who is
 * speaking. Atlas uses hyphenated surface ids ('gemini-enterprise'); the graph
 * carries whatever the ingestion source stamped ('gemini_enterprise',
 * 'microsoft_365_copilot'); ingestion sources name themselves a third way
 * ('gemini-enterprise-traces'). All three mean one vendor mark.
 *
 * Keeping that as one table here — rather than a switch inside each consumer —
 * is what makes a new surface a single-line change: add its aliases, and every
 * badge, node and legend that renders a mark picks it up.
 * ------------------------------------------------------------------------- */
const SURFACE_ALIASES = {
  copilot:             ['copilot', 'microsoft_365_copilot', 'm365_copilot', 'copilot-traces', 'copilot_traces'],
  'copilot-studio':    ['copilot-studio', 'copilot_studio'],
  // Only spellings that NAME a vendor belong here. Inventory sources such as
  // agent-registry and posture-snapshots report Gemini agents today but are not
  // Gemini-specific, and mapping them would stamp the wrong logo the first time
  // one covers another vendor.
  'gemini-enterprise': ['gemini-enterprise', 'gemini_enterprise', 'gemini-enterprise-traces'],
  'gemini-workspace':  ['gemini-workspace', 'gemini_workspace'],
  custom:              ['custom', 'otel', 'otel_traces', 'otel-traces', 'sdk_telemetry'],
  shadow:              ['shadow', 'endpoint', 'endpoint_events', 'endpoint-events'],
  gcp:                 ['gcp', 'gcp_audit', 'gcp-traces', 'gcp_traces', 'vertex'],
  aws:                 ['aws', 'bedrock', 'aws_bedrock'],
  azure:               ['azure', 'azure_openai', 'azure-openai'],
  anthropic:           ['anthropic', 'anthropic_admin', 'claude'],
  openai:              ['openai', 'openai_admin', 'chatgpt'],
  cursor:              ['cursor'],
  mdm:                 ['jamf', 'intune', 'mdm_inventory'],
};

const ALIAS_TO_SURFACE = Object.entries(SURFACE_ALIASES).reduce((acc, [surface, aliases]) => {
  aliases.forEach((a) => { acc[a] = surface; });
  return acc;
}, {});

/**
 * Resolve any surface spelling to the key ICONS is registered under.
 * Returns null when the vendor genuinely isn't known — callers should render
 * nothing rather than guess, since a wrong vendor mark is worse than none.
 */
export const resolveSurface = (token) => {
  if (!token) return null;
  const k = String(token).trim().toLowerCase();
  if (ICONS[k]) return k;
  return ALIAS_TO_SURFACE[k] || null;
};

/**
 * First recognizable vendor in a comma-separated surface list, plus everything
 * that resolved. Graph nodes carry several ('gemini_enterprise, gcp_audit'):
 * the first drives the mark, the full list drives the tooltip.
 */
export const surfacesFrom = (value) => {
  const raw = Array.isArray(value) ? value : String(value || '').split(/[,;]/);
  const seen = [];
  raw.forEach((t) => {
    const s = resolveSurface(t);
    if (s && !seen.includes(s)) seen.push(s);
  });
  return seen;
};
